/**
 * BankPage.jsx — /bank route. Full ledger with filter chips
 * (all / earns / spends) and source dropdown. Polls on mount only;
 * user can pull-to-refresh by clicking the refresh button.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Coins, ArrowUpRight, ArrowDownRight, Loader2, ArrowLeft, Wallet,
  RefreshCw, TrendingUp, TrendingDown, AlertTriangle,
} from 'lucide-react';
import { authFetch, formatApiError } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 30;

// Financial values remain unknown if the server omits or malforms them; never
// substitute a local zero that could look like a real balance.
const displayNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toLocaleString() : '—';
};

const displayDelta = (value, prefix) => {
  const formatted = displayNumber(value);
  return formatted === '—' ? formatted : `${prefix}${formatted}`;
};

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const FilterChip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-[11px] uppercase tracking-widest font-extrabold px-3 py-1.5 rounded-full border transition-all ${
      active
        ? 'bg-amber-400 border-amber-400 text-slate-900'
        : 'bg-transparent border-white/15 text-white/55 hover:border-white/40 hover:text-white'
    }`}
    data-testid={testid}
  >
    {children}
  </button>
);

const LedgerRow = ({ entry }) => {
  const isEarn = entry.amount >= 0;
  const Icon = isEarn ? ArrowDownRight : ArrowUpRight;
  return (
    <article
      className="flex items-center gap-3 py-3 px-4 hover:bg-white/[0.02] transition-colors"
      data-testid={`bank-page-row-${entry.created_at}`}
    >
      <div className={`w-9 h-9 rounded-lg flex-shrink-0 grid place-items-center border ${
        isEarn ? 'bg-emerald-500/10 border-emerald-400/30' : 'bg-rose-500/10 border-rose-400/30'
      }`}>
        <Icon className={`w-4 h-4 ${isEarn ? 'text-emerald-300' : 'text-rose-300'}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-white">
          {entry.label || entry.source}
        </p>
        <p className="text-[11px] text-white/40">
          {formatTime(entry.created_at)} · <span className="uppercase tracking-wider">{entry.source}</span>
        </p>
      </div>
      <div className="text-right">
        <p className={`tabular-nums text-base font-extrabold ${
          isEarn ? 'text-emerald-300' : 'text-rose-300'
        }`}>
          {isEarn ? '+' : ''}{entry.amount.toLocaleString()}
        </p>
        <p className="text-[10px] text-white/30 tabular-nums">
          balance {entry.balance_after.toLocaleString()}
        </p>
      </div>
    </article>
  );
};

const BankPage = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState('');
  const [direction, setDirection] = useState(''); // '' | 'earn' | 'spend'
  const [refreshing, setRefreshing] = useState(false);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError('');
    try {
      const r = await authFetch(`${BACKEND_URL}/api/bank/me`);
      const data = await r.json();
      if (!r.ok) throw new Error(formatApiError(data.detail));
      setSummary(data);
    } catch (err) {
      setSummary(null);
      setSummaryError(typeof err?.message === 'string' ? err.message : 'Could not load your current balance.');
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const fetchPage = useCallback(async (reset = false) => {
    setError('');
    if (reset) setLoading(true);
    else setLoadingMore(true);
    const skip = reset ? 0 : entries.length;
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), skip: String(skip) });
    if (direction) params.set('direction', direction);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/bank/me/ledger?${params}`);
      const data = await r.json();
      if (!r.ok) throw new Error(formatApiError(data.detail));
      const nextEntries = Array.isArray(data.entries) ? data.entries : [];
      setEntries((prev) => reset ? nextEntries : [...prev, ...nextEntries]);
      setHasMore(nextEntries.length >= PAGE_SIZE);
    } catch (err) {
      setError(typeof err?.message === 'string' ? err.message : 'Failed to load ledger');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [direction, entries.length]);

  useEffect(() => {
    fetchSummary();
    fetchPage(true);
    // The filter is the fetch boundary. The callbacks deliberately change as
    // pagination state changes, and including them here would refetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction]);

  const refresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchSummary(), fetchPage(true)]);
    setRefreshing(false);
  };

  const setFilter = (nextDirection) => {
    if (nextDirection === direction) return;
    // Do not leave transactions from the previous filter visible while the
    // server-confirmed ledger for the new filter is loading.
    setEntries([]);
    setHasMore(true);
    setDirection(nextDirection);
  };

  return (
    <main className="sharp-bg max-w-2xl mx-auto px-4 py-6 pb-32 min-h-screen" data-testid="bank-page">
      <div className="flex items-center justify-between mb-5">
        <button
          type="button"
          onClick={() => navigate('/profile')}
          className="text-white/60 hover:text-white inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold"
          data-testid="bank-page-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Profile
        </button>
        <p className="eyebrow text-amber-300">Coin bank</p>
        <button
          type="button"
          onClick={refresh}
          disabled={refreshing}
          className="text-white/60 hover:text-white disabled:opacity-50 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold"
          data-testid="bank-page-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* This card renders only server-returned totals; it never derives or predicts a balance client-side. */}
      {summaryLoading && (
        <section className="rounded-2xl border border-white/10 bg-[#12131A] p-5 mb-5" role="status" aria-label="Loading current coin balance" data-testid="bank-page-summary-loading">
          <div className="h-3 w-20 rounded bg-white/10 animate-pulse mb-3" />
          <div className="h-10 w-40 rounded bg-white/10 animate-pulse" />
        </section>
      )}
      {!summaryLoading && summaryError && (
        <section className="rounded-2xl border border-rose-400/40 bg-rose-500/10 p-4 mb-5 flex items-start gap-3" role="alert" data-testid="bank-page-summary-error">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-rose-300" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-rose-100">Current balance is unavailable.</p>
            <p className="mt-1 text-xs text-rose-100/80">{summaryError}</p>
          </div>
          <button type="button" onClick={fetchSummary} className="shrink-0 rounded-md border border-rose-300/50 px-2.5 py-1.5 text-xs font-bold text-rose-100 hover:bg-rose-400/10" data-testid="bank-page-summary-retry">Retry</button>
        </section>
      )}
      {!summaryLoading && summary && (
        <section
          className="relative overflow-hidden rounded-2xl border border-amber-400/30 bg-gradient-to-br from-[#171521] via-[#12131A] to-amber-950/30 p-5 mb-5"
          data-testid="bank-page-summary"
        >
          <div className="pointer-events-none absolute -right-10 -top-12 h-36 w-36 rounded-full bg-amber-400/10 blur-3xl" aria-hidden="true" />
          <div className="relative flex items-end justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] font-extrabold text-white/50 mb-1">Server-confirmed balance</p>
              <p className="font-display text-5xl font-black text-white leading-none tabular-nums inline-flex items-center gap-2">
                <Coins className="w-8 h-8 text-amber-300" aria-hidden="true" />
                {displayNumber(summary.balance)}
              </p>
            </div>
            <div className="text-right space-y-1">
              <p className="text-xs font-bold inline-flex items-center gap-1 text-emerald-300">
                <TrendingUp className="w-3.5 h-3.5" aria-hidden="true" />
                {displayDelta(summary.lifetime_earned, '+')}
              </p>
              <p className="text-xs font-bold inline-flex items-center gap-1 text-rose-300">
                <TrendingDown className="w-3.5 h-3.5" aria-hidden="true" />
                {displayDelta(summary.lifetime_spent, '-')}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Filter row */}
      <div className="flex items-center gap-2 mb-4" data-testid="bank-page-filters">
        <FilterChip active={direction === ''} onClick={() => setFilter('')} testid="bank-filter-all">
          All
        </FilterChip>
        <FilterChip active={direction === 'earn'} onClick={() => setFilter('earn')} testid="bank-filter-earn">
          Earns
        </FilterChip>
        <FilterChip active={direction === 'spend'} onClick={() => setFilter('spend')} testid="bank-filter-spend">
          Spends
        </FilterChip>
      </div>

      {/* Ledger list */}
      <section className="rounded-2xl border border-white/10 bg-[#0E0F15] overflow-hidden" data-testid="bank-page-ledger">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-white/55 p-6 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading ledger…
          </div>
        ) : error ? (
          <div className="p-6 text-sm text-rose-100" role="alert" data-testid="bank-page-error">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-rose-300" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="font-bold">Transaction history is unavailable.</p>
                <p className="mt-1 text-rose-100/75">{error}</p>
              </div>
              <button type="button" onClick={() => fetchPage(true)} className="shrink-0 rounded-md border border-rose-300/50 px-2.5 py-1.5 text-xs font-bold hover:bg-rose-400/10" data-testid="bank-page-retry">Try again</button>
            </div>
          </div>
        ) : entries.length === 0 ? (
          <div className="p-8 text-center text-sm text-white/50" data-testid="bank-page-empty">
            <Wallet className="w-7 h-7 text-white/20 mx-auto mb-2" />
            No transactions in this view yet.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {entries.map((e, i) => (
              <LedgerRow key={`${e.created_at}-${i}`} entry={e} />
            ))}
          </div>
        )}
        {hasMore && !loading && entries.length > 0 && (
          <button
            type="button"
            onClick={() => fetchPage(false)}
            disabled={loadingMore}
            className="w-full px-4 py-3 text-[11px] uppercase tracking-widest font-extrabold text-white/55 hover:text-white hover:bg-white/[0.03] border-t border-white/5 disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="bank-page-load-more"
          >
            {loadingMore ? <span className="inline-flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Loading more…</span> : 'Load more'}
          </button>
        )}
      </section>
    </main>
  );
};

export default BankPage;
